import { useEffect, useMemo } from 'react';
import { App } from 'antd';
import {
    CheckCircleBoldIcon,
    InfoCircleBoldIcon,
    DangerTriangleBoldIcon,
    CloseCircleBoldIcon,
    CloseBoldIcon,
} from '@solar-icons/react';
import './Notification.module.css';

const TYPE_ICONS = {
    success: <CheckCircleBoldIcon color="var(--color-feedback-success-text)" />,
    info: <InfoCircleBoldIcon color="var(--color-primary-700)" />,
    warning: <DangerTriangleBoldIcon color="var(--color-feedback-warning-text)" />,
    error: <CloseCircleBoldIcon color="var(--color-feedback-error-text)" />,
};

const DEFAULT_CLOSE_ICON = <CloseBoldIcon size={18} color="var(--color-text-secondary)" aria-hidden="true" />;

const TYPE_BACKGROUNDS = {
    success: 'var(--color-feedback-success-bg)',
    info: 'var(--color-primary-100)',
    warning: 'var(--color-feedback-warning-bg)',
    error: 'var(--color-feedback-error-bg)',
};

// antd hardcodes role="alert" (assertive) for every type. Routine confirmations shouldn't
// interrupt a screen reader mid-sentence, so only warning/error keep the assertive role.
const TYPE_ROLES = {
    success: 'status',
    info: 'status',
    warning: 'alert',
    error: 'alert',
};

const buildTypeOpen = (open, type) => (args = {}) => open({
    icon: TYPE_ICONS[type],
    closeIcon: DEFAULT_CLOSE_ICON,
    role: TYPE_ROLES[type],
    style: { background: TYPE_BACKGROUNDS[type] },
    ...args,
    type,
});

const buildNotificationApi = antdNotification => ({
    ...antdNotification,
    success: buildTypeOpen(antdNotification.open, 'success'),
    info: buildTypeOpen(antdNotification.open, 'info'),
    warning: buildTypeOpen(antdNotification.open, 'warning'),
    error: buildTypeOpen(antdNotification.open, 'error'),
});

let activeApi = null;

const callActive = method => (...args) => {
    if (!activeApi) {
        console.warn(`vector notification.${method}() called before ConfigProvider mounted — notification was dropped`);
        return undefined;
    }
    return activeApi[method](...args);
};

// Module-level singleton so callers can `import { notification } from 'vector'` with no hook of
// their own — NotificationBridge below is what keeps it pointed at the live, themed antd instance.
const notification = {
    /** @param {Object} config - Same shape as antd's own `notification.open` config. */
    open: callActive('open'),
    /**
     * @param {Object} config - Same shape as antd's own `notification.success` config.
     * @param {string} config.title - Notification heading.
     * @param {string} [config.description] - Longer body text.
     * @param {number|false} [config.duration] - Seconds before auto-dismiss; 0/false (the
     *   default) leaves it open until manually closed.
     * @param {Function} [config.onClose] - Called when the notification closes.
     */
    success: callActive('success'),
    /** @see notification.success - same call shape. */
    info: callActive('info'),
    /** @see notification.success - same call shape. */
    warning: callActive('warning'),
    /** @see notification.success - same call shape. */
    error: callActive('error'),
    /** @param {string|number} [key] - Closes one notification by key, or every one if omitted. */
    destroy: callActive('destroy'),
};

export const NotificationBridge = () => {
    const { notification: antdNotification } = App.useApp();
    const api = useMemo(() => buildNotificationApi(antdNotification), [antdNotification]);

    useEffect(() => {
        activeApi = api;
        return () => {
            activeApi = null;
        };
    }, [api]);

    return null;
};

export default notification;
